<template>
  <div class="consent-signing-container">
    <div class="service-header flex-column mb-3 d-flex">
      <h6 class="fs-6 fw-semibold text-dark mb-1 mt-0">Consent Document Signing</h6>
      <p class="text-xs text-muted mb-0 lh-sm">Please review and sign the consent document to proceed with your background screening.</p>
    </div>

    <!-- Loading configuration -->
    <div v-if="loading_config" class="loading-section">
      <div class="loading-spinner"></div>
      <label class="fs-6 fw-bold text-dark">Loading Configuration</label>
      <p class="text-xs fw-normal text-muted">Please wait while we prepare your consent options...</p>
    </div>

    <!-- Consent Options -->
    <div v-else-if="consent_config && !embed_src" class="consent-options mb-4">
      
      <!-- Email Signature Option -->
      <div v-if="consent_config.send_email_signature" class="consent-option mb-4">
        <div class="option-header">
          <h6 class="fw-semibold text-dark mb-2">
            <i class="fas fa-envelope text-primary me-2"></i>
            Email Signature Required
          </h6>
          <p class="text-xs text-muted mb-3">
            You will receive an email with the consent form to complete and sign digitally.
          </p>
        </div>
        
        <div class="email-input-section">
          <div class="row g-3">
            <div class="col-md-8">
              <label class="form-label text-xs fw-semibold">Email Address</label>
              <input
                type="email"
                class="form-control"
                v-model="customer_email"
                placeholder="Enter your email address"
                :disabled="email_sent"
              />
            </div>
            <div class="col-md-4 d-flex align-items-end">
              <button
                type="button"
                class="btn btn-bg-green text-xs rounded-3 w-100"
                @click="send_email_consent"
                :disabled="sending_email || !is_valid_email || email_sent"
              >
                <i class="fas fa-spinner fa-spin me-1" v-if="sending_email"></i>
                <i class="fas fa-check me-1" v-else-if="email_sent"></i>
                <i class="fas fa-paper-plane me-1" v-else></i>
                {{ email_sent ? 'Email Sent' : 'Send Email' }}
              </button>
            </div>
          </div>
          
          <div v-if="email_sent" class="alert alert-success mt-3">
            <i class="fas fa-check-circle me-2"></i>
            Consent form has been sent to {{ customer_email }}. Please check your email and complete the form.
          </div>
        </div>
      </div>

      <!-- Manual Upload Option -->
      <div v-if="consent_config.manual_upload" class="consent-option mb-4">
        <div class="option-header">
          <h6 class="fw-semibold text-dark mb-2">
            <i class="fas fa-upload text-success me-2"></i>
            Manual Form Upload
          </h6>
          <p class="text-xs text-muted mb-3">
            Download the consent form, print it, sign it manually, and upload it back to the system.
          </p>
        </div>
        
        <div class="download-section">
          <button
            type="button"
            class="btn btn-bg-green px-4 py-2 rounded-3 fw-bold text-xxs me-2"
            @click="generate_manual_form"
            :disabled="generating_manual"
          >
            <i class="fas fa-spinner fa-spin me-1" v-if="generating_manual"></i>
            <i class="fas fa-file-pdf me-1" v-else></i>
            {{ generating_manual ? 'Generating...' : 'Download Consent Form' }}
          </button>
          
          <div v-if="manual_form_generated" class="mt-3">
            <!-- File Upload Section -->
            <div class="upload-section mt-3">
              <label class="file-upload-label mb-1">Upload Signed Consent Form</label>
              <small class="text-xs text-muted mb-3 d-block">
                <b class="text-danger">Important:</b> Please upload the signed PDF document only.
              </small>
              <hr class="mb-2 fw-light" />
              <div class="file-upload-container w-100 mx-auto">
                <div class="custom-file-upload">
                  <div
                    class="upload-header"
                    :class="{ 'has-file': selected_file }"
                    @dragover.prevent="isDragOver = true"
                    @dragleave.prevent="isDragOver = false"
                    @drop.prevent="handle_file_drop"
                  >
                    <div v-if="!selected_file" class="upload-placeholder">
                      <p class="mb-2 mt-3 fw-bold text-xs">
                        Drag and drop your signed consent form here
                      </p>
                      <p class="text-muted small text-xxs mb-3">or</p>
                      <button
                        type="button"
                        class="btn btn-bg-green px-4 py-2 rounded-small fw-bold text-xxs"
                        @click="$refs.fileInput.click()"
                      >
                        Browse Files
                      </button>
                      <p class="text-muted mt-3 small text-xxs">
                        Supported format: PDF
                      </p>
                      <p class="text-muted small text-xxs">
                        Maximum file size: 50MB
                      </p>
                    </div>
                    <div v-else class="selected-files-container">
                      <div class="my-1 text-center">
                        <p class="fw-bold text-xs">
                          <i class="fas fa-check-circle text-success me-2"></i>
                          1 file successfully selected
                        </p>
                      </div>
                      <div class="selected-files-list">
                        <div class="selected-file-item mb-2">
                          <div class="d-flex align-items-center flex-grow-1 overflow-hidden">
                            <i class="fas fa-file-alt me-2 text-primary flex-shrink-0"></i>
                            <span class="text-truncate file-name" :title="selected_file.name"
                              >{{ selected_file.name }}</span
                            >
                          </div>
                          <div class="file-actions">
                            <span class="file-size me-2 text-muted text-xxs"
                              >{{ format_file_size(selected_file.size) }}</span
                            >
                            <button
                              type="button"
                              class="btn btn-sm text-danger p-0"
                              @click="remove_file"
                              title="Remove file"
                            >
                              <i class="fas fa-times-circle"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <input
                    :ref="'fileInput'"
                    type="file"
                    class="d-none"
                    @change="handle_file_select"
                    accept=".pdf"
                  />
                </div>
                <small
                  v-if="upload_error"
                  class="form-text text-danger mt-2 d-block"
                >
                  {{ upload_error }}
                </small>
              </div>
              
              <div class="text-center mt-3">
                <button
                  type="button"
                  class="btn btn-bg-green text-xs btn-sm rounded-3 px-4 py-2"
                  @click="upload_signed_form"
                  :disabled="!selected_file || uploading"
                >
                  <i class="fas fa-spinner fa-spin me-1" v-if="uploading"></i>
                  <i class="fas fa-upload me-1" v-else></i>
                  {{ uploading ? 'Uploading...' : 'Upload Signed Form' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Portal Signing (Default) -->
      <div v-if="!consent_config.send_email_signature && !consent_config.manual_upload" class="consent-option mb-4">
        <div class="option-header">
          <h6 class="fw-semibold text-dark mb-2">
            <i class="fas fa-edit text-info me-2"></i>
            Digital Signature
          </h6>
          <p class="text-xs text-muted mb-3">
            Complete and sign your consent form digitally right here in the portal.
          </p>
        </div>
        
        <div class="portal-section">
          <button
            type="button"
            class="btn btn-bg-green text-xs rounded-3"
            @click="start_portal_signing"
            :disabled="loading"
          >
            <i class="fas fa-spinner fa-spin me-1" v-if="loading"></i>
            <i class="fas fa-pen me-1" v-else></i>
            {{ loading ? 'Loading...' : 'Start Digital Signing' }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- Loading state for signing -->
    <div v-else-if="loading" class="loading-section">
      <div class="loading-spinner"></div>
      <label class="fs-6 fw-bold text-dark">Preparing Document</label>
      <p class="text-xs fw-normal text-muted">Please wait while we prepare your consent document...</p>
    </div>
    
    <!-- DocuSeal Form -->
    <div v-else-if="embed_src" class="docuseal-form-container">
      <DocusealForm
        :src="embed_src"
        @completed="handle_form_completed"
        @error="handle_form_error"
      />
    </div>
    
    <!-- Error state -->
    <div v-else-if="error" class="error-section">
      <i class="fas fa-exclamation-triangle text-warning fs-6"></i>
      <label class="fs-6 fw-bold text-dark">Unable to load document</label>
      <p>{{ error }}</p>
      <button @click="initialize_signing" class="btn btn-bg-green text-xs rounded-3 btn-sm">
        <i class="fas fa-redo"></i>
        Try Again
      </button>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from "@/config";
import { DocusealForm } from '@docuseal/vue';
import Swal from "sweetalert2";

export default {
  name: "DocusealSigning",
  components: {
    DocusealForm
  },
  computed: {
    workflow_id() {
      return localStorage.getItem("workflow_id") || null;
    },
    is_valid_email() {
      const email_regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return email_regex.test(this.customer_email);
    }
  },
  data() {
    return {
      loading: false,
      loading_config: false,
      embed_src: null,
      submission_id: null,
      error: null,
      is_completed: false,
      
      // Consent configuration
      consent_config: null,
      
      // Email workflow
      customer_email: "",
      sending_email: false,
      email_sent: false,
      
      // Manual workflow
      generating_manual: false,
      manual_form_generated: false,
      manual_download_url: "",
      
      // File upload
      selected_file: null,
      isDragOver: false,
      uploading: false,
      upload_error: null,
    };
  },
  methods: {
    async initialize_signing() {
      this.loading_config = true;
      this.error = null;
      
      try {
        await this.load_consent_configuration();
        
        // If no special configuration, proceed with default portal signing
        if (!this.consent_config.send_email_signature && !this.consent_config.manual_upload) {
          await this.start_portal_signing();
        }
      } catch (err) {
        console.error("Failed to initialize signing:", err);
        this.error = "Failed to initialize consent form. Please try again.";
      } finally {
        this.loading_config = false;
      }
    },

    async load_consent_configuration() {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/verify_api/employee_screening/api/consent/configuration/?workflow_id=${this.workflow_id}`,
        {
          method: "GET",
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );
      
      const data = await response.json();
      
      if (data.status === "success") {
        this.consent_config = data.data;
        // Pre-fill email if available
        if (this.consent_config.consultant_info?.email) {
          this.customer_email = this.consent_config.customer_email || this.consent_config.consultant_info.email;
        }
        // Set email sent status if already sent
        if (this.consent_config.has_existing_submission && 
            this.consent_config.method === "Email") {
          this.email_sent = true;
          this.customer_email = this.consent_config.customer_email;
        }
      } else {
        this.error = data.message || "Failed to load consent configuration";
      }
    },
    
    async send_email_consent() {
      if (!this.is_valid_email) {
        Swal.fire({
          icon: "error",
          title: "Please enter a valid email address",
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
        });
        return;
      }
      
      this.sending_email = true;
      
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_BASE_URL}/verify_api/employee_screening/api/consent/email/send/`,
          {
            method: "POST",
            headers: {
              Authorization: `Token ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              workflow_id: this.workflow_id,
              customer_email: this.customer_email
            }),
          }
        );
        
        const data = await response.json();
        
        if (data.status === "success") {
          this.email_sent = true;
          Swal.fire({
            icon: "success",
            title: "Consent form sent successfully!",
            text: `Please check your email at ${this.customer_email}`,
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
          });
          
          this.$emit('consent_completed', {
            success: true,
            method: 'email',
            email: this.customer_email,
            submission_id: data.data.submission_id
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Failed to send email",
            text: data.message,
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
          });
        }
      } catch (err) {
        console.error("Error sending email consent:", err);
        Swal.fire({
          icon: "error",
          title: "Network error occurred",
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
        });
      } finally {
        this.sending_email = false;
      }
    },
    
    async generate_manual_form() {
      this.generating_manual = true;
      
      try {
        // Check if there's already an existing manual submission
        if (this.consent_config.has_existing_submission && 
            this.consent_config.method === "In Person" && 
            this.consent_config.submission_id) {
          
          // Download existing PDF instead of generating new one
          await this.download_existing_manual_form(this.consent_config.submission_id);
          
        } else {
          // Generate new manual form
          await this.create_new_manual_form();
        }
        
      } catch (err) {
        console.error("Error in generate_manual_form:", err);
        Swal.fire({
          icon: "error",
          title: "Network error occurred",
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
        });
      } finally {
        this.generating_manual = false;
      }
    },

    async download_existing_manual_form(submission_id) {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_BASE_URL}/verify_api/employee_screening/api/consent/download/?submission_id=${submission_id}`,
          {
            method: "GET",
            headers: {
              Authorization: `Token ${token}`,
            },
          }
        );
        
        const data = await response.json();
        
        if (data.status === "success") {
          this.manual_form_generated = true;
          this.manual_download_url = data.data.pdf_url;
          this.submission_id = submission_id;
          
          // Automatically download the PDF
          if (this.manual_download_url) {
            this.download_file(this.manual_download_url, 'consent_form.pdf');
          }
          
          Swal.fire({
            icon: "success",
            title: "Consent form downloaded successfully!",
            text: "The existing PDF has been downloaded automatically. Please print, sign, and upload it.",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Failed to download existing form",
            text: data.message,
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
          });
        }
      } catch (err) {
        console.error("Error downloading existing manual form:", err);
        throw err;
      }
    },

    async create_new_manual_form() {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_BASE_URL}/verify_api/employee_screening/api/consent/manual/generate/`,
          {
            method: "POST",
            headers: {
              Authorization: `Token ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              workflow_id: this.workflow_id
            }),
          }
        );
        
        const data = await response.json();
        
        if (data.status === "success") {
          this.manual_form_generated = true;
          this.manual_download_url = data.data.pdf_url || data.data.download_url;
          this.submission_id = data.data.submission_id;
          
          // Automatically download the PDF
          if (this.manual_download_url) {
            this.download_file(this.manual_download_url, 'consent_form.pdf');
          }
          
          Swal.fire({
            icon: "success",
            title: "Consent form generated successfully!",
            text: "The PDF has been downloaded automatically. Please print, sign, and upload it.",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
          });
          
          // Note: We don't emit consent_completed here anymore
          // It will be emitted after successful upload
        } else {
          Swal.fire({
            icon: "error",
            title: "Failed to generate form",
            text: data.message,
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
          });
        }
      } catch (err) {
        console.error("Error creating new manual form:", err);
        throw err;
      }
    },

    handle_file_select(event) {
      if (event.target.files && event.target.files.length > 0) {
        const file = event.target.files[0];
        this.process_file(file);
      }
      // Clear the input
      event.target.value = "";
    },

    handle_file_drop(event) {
      this.isDragOver = false;
      const files = event.dataTransfer.files;
      if (files && files.length > 0) {
        const file = files[0];
        this.process_file(file);
      }
    },

    process_file(file) {
      this.upload_error = null;

      // Validate file type
      const valid_types = ["application/pdf"];
      if (!valid_types.includes(file.type)) {
        this.upload_error = "Please select a PDF file only.";
        Swal.fire({
          icon: "error",
          title: "Invalid file type",
          text: "Please select a PDF file.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        return;
      }

      // Validate file size (50MB limit)
      const max_size = 50 * 1024 * 1024;
      if (file.size > max_size) {
        this.upload_error = "File size exceeds 50MB limit.";
        Swal.fire({
          icon: "error",
          title: "File too large",
          text: "Please select a file smaller than 50MB.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        return;
      }

      this.selected_file = file;
    },

    remove_file() {
      this.selected_file = null;
      this.upload_error = null;
    },

    format_file_size(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    async upload_signed_form() {
      if (!this.selected_file) {
        Swal.fire({
          icon: "error",
          title: "No file selected",
          text: "Please select a file to upload.",
          showConfirmButton: false,
          timer: 2000,
        });
        return;
      }

      this.uploading = true;

      try {
        const token = localStorage.getItem("token");
        const formData = new FormData();
        formData.append('workflow_id', this.workflow_id);
        formData.append('submission_id', this.submission_id);
        formData.append('signed_consent_form', this.selected_file);

        const response = await fetch(
          `${API_BASE_URL}/verify_api/employee_screening/api/consent/upload/`,
          {
            method: "POST",
            headers: {
              Authorization: `Token ${token}`,
            },
            body: formData,
          }
        );

        const data = await response.json();

        if (data.status === "success") {
          Swal.fire({
            icon: "success",
            title: "Upload successful!",
            text: "Your signed consent form has been uploaded successfully.",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
          });

          // Emit completion event now that upload is successful
          this.$emit('consent_completed', {
            success: true,
            method: 'manual_upload',
            submission_id: this.submission_id,
            document_id: data.data.document_id,
            file_path: data.data.file_path
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Upload failed",
            text: data.message,
            showConfirmButton: false,
            timer: 3000,
          });
        }
      } catch (err) {
        console.error("Error uploading signed form:", err);
        Swal.fire({
          icon: "error",
          title: "Network error occurred",
          text: "Please check your connection and try again.",
          showConfirmButton: false,
          timer: 3000,
        });
      } finally {
        this.uploading = false;
      }
    },

    async start_portal_signing() {
      this.loading = true;
      
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_BASE_URL}/verify_api/employee_screening/api/consent/portal/create/`, {
          method: "POST",
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            workflow_id: this.workflow_id,
          }),
        });
        
        const data = await response.json();
        
        if (data.status !== "success") {
          this.error = data.message || "Failed to create consent submission";
          return;
        }
        
        this.embed_src = data.data.embed_src;
        this.submission_id = data.data.submission_id;
        
      } catch (err) {
        console.error("Error creating consent submission:", err);
        this.error = "Network error occurred. Please check your connection and try again.";
      } finally {
        this.loading = false;
      }
    },
    
    async handle_form_completed() {
      this.is_completed = true;
      
      try {
        await this.get_submitted_document();
        
        Swal.fire({
          icon: "success",
          title: "Consent form completed successfully!",
          text: "Thank you for completing your consent form.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        
        // Emit completion event to parent component
        this.$emit('consent_completed', {
          success: true,
          method: 'portal',
          submission_id: this.submission_id
        });
        
      } catch (err) {
        console.error("Error handling form completion:", err);
        this.$emit('consent_completed', {
          success: false,
          error: "Failed to retrieve signed document"
        });
      }
    },
    
    async get_submitted_document() {
      const token = localStorage.getItem("token");
      
      const response = await fetch(`${API_BASE_URL}/verify_api/employee_screening/api/consent/download/?submission_id=${this.submission_id}`, {
        method: "GET",
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      
      const data = await response.json();
      
      if (data.status !== "success") {
        throw new Error(data.message || "Failed to retrieve submitted document");
      }
      
      return data.data;
    },
    
    handle_form_error(error) {
      console.error("DocuSeal form error:", error);
      this.error = "An error occurred while loading the document form. Please try again.";
      
      Swal.fire({
        icon: "error",
        title: "Error with consent form",
        text: "An error occurred while processing your consent form. Please try again.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      
      this.$emit('consent_completed', {
        success: false,
        error: this.error
      });
    },
    
    handle_data_submit() {
      // This method is called by the parent component to validate the step
      return this.is_completed;
    },

    download_file(url, filename) {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.target = '_blank';
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  },
  
  mounted() {
    // Auto-initialize the signing process when component is mounted
    this.initialize_signing();
  },
};
</script>

<style scoped>
.consent-signing-container {
  max-width: 800px;
  margin: 0 auto;
}

.service-header {
  padding-bottom: 1rem;
  border-bottom: 1px solid #f1f3f4;
  margin-bottom: 1.5rem;
}

.loading-section,
.error-section,
.initial-section {
  text-align: center;
  padding: 60px 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e9ecef;
  border-top: 3px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 24px;
}

.loading-section h3,
.error-section h3,
.initial-section h3 {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
}

.loading-section p,
.error-section p,
.initial-section p {
  color: #6c757d;
  font-size: 14px;
  margin-bottom: 20px;
}

.docuseal-form-container {
  min-height: 600px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

/* Consent Options Styling */
.consent-option {
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 1.5rem;
  background: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
}

.option-header {
  margin-bottom: 1rem;
}

.email-input-section,
.download-section,
.portal-section {
  padding-top: 1rem;
  border-top: 1px solid #f1f3f4;
}

.existing-submission {
  border-radius: 8px;
  overflow: hidden;
}

.fw-semibold {
  font-weight: 600;
}

.text-xxs {
  font-size: 10px !important;
}

.text-xs {
  font-size: 12px !important;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* File Upload Styles */
.file-upload-container {
  margin-bottom: 1rem;
}

.custom-file-upload {
  border: 2px dashed #ced4da;
  border-radius: 12px;
  transition: all 0.3s ease;
  min-height: 200px;
}

.custom-file-upload:hover {
  border-color: var(--primary-color);
}

.custom-file-upload.drag-over {
  border-color: var(--primary-color);
}

.upload-header {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 180px;
}

.upload-header.has-file {
  padding: 1.5rem;
  min-height: auto;
}

.upload-placeholder {
  text-align: center;
}

.selected-files-container {
  width: 100%;
}

.selected-files-list {
  max-height: 200px;
  overflow-y: auto;
  margin: 1rem 0;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
}

.selected-file-item {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.75rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.file-name {
  max-width: 200px;
  font-size: 0.875rem;
  font-weight: 500;
  word-break: break-word;
}

@media (max-width: 576px) {
  .selected-file-item {
    padding: 0.5rem;
  }
  
  .file-name {
    max-width: 150px;
    font-size: 0.8rem;
  }
}

.file-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.25rem;
}

.file-size {
  font-size: 0.75rem;
  white-space: nowrap;
}

@media (max-width: 576px) {
  .file-actions {
    width: 100%;
    justify-content: flex-end;
    margin-top: 0.25rem;
  }
  
  .file-size {
    margin-right: 0.5rem !important;
  }
}

.file-upload-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--bs-dark);
}

/* Temp CSS until radio buttons are fixed */
.form-check-label {
  font-size: 0.875rem !important;
  font-weight: 500 !important;
  color: var(--bs-dark) !important;
  display: flex !important;
  align-items: center !important;
  gap: 0.5rem !important;
  margin-left: 1.5rem !important;
}

/* File actions styling */
.file-actions button {
  transition: all 0.2s ease;
}

.file-actions button:hover {
  transform: scale(1.1);
}

.badge {
  font-size: 0.65rem;
  padding: 0.25rem 0.5rem;
}

/* Responsive styles for mobile */
@media (max-width: 576px) {
  .custom-file-upload {
    min-height: 180px;
  }
  
  .upload-header {
    padding: 1rem;
    min-height: 150px;
  }
  
  .upload-header.has-file {
    padding: 0.75rem;
  }
  
  .selected-files-list {
    max-height: 180px;
  }
  
  button.btn-bg-green {
    padding: 0.375rem 0.75rem !important;
  }
}
</style>
